export type SyncDiagnosticLevel = 'info' | 'warn' | 'error';

export interface SyncDiagnosticEvent {
  id: string;
  at: string;
  level: SyncDiagnosticLevel;
  source: string;
  message: string;
  details: Record<string, string | number | boolean | null>;
}

const STORAGE_KEY = 'maximumBand.syncDiagnostics.v1';
const MAX_EVENTS = 80;
const listeners = new Set<() => void>();

function safeRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readEvents(): SyncDiagnosticEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SyncDiagnosticEvent[]) : [];
  } catch {
    return [];
  }
}

function writeEvents(events: SyncDiagnosticEvent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(0, MAX_EVENTS)));
  } catch {
    // Diagnostics must never affect app behavior.
  }
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function addSyncDiagnostic(event: Omit<SyncDiagnosticEvent, 'id' | 'at'>): void {
  writeEvents([{ ...event, id: safeRandomId(), at: new Date().toISOString() }, ...readEvents()]);
  notify();
}

export function getSyncDiagnostics(): SyncDiagnosticEvent[] {
  return readEvents();
}

export function clearSyncDiagnostics(): void {
  writeEvents([]);
  notify();
}

export function subscribeSyncDiagnostics(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function shortSyncId(value: string | null | undefined): string {
  if (!value) return 'none';
  return value.length <= 12 ? value : `${value.slice(0, 8)}...${value.slice(-4)}`;
}
